/**
 * AgentNodeToolbar -- Floating pill-shaped toolbar for agent nodes
 *
 * Positioned absolutely above the node. Uses inverse zoom scaling
 * to maintain readable size when zoomed out.
 * Springs up with fade-in animation when node is selected.
 */

import { memo } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../../../lib/utils'

export interface ToolbarAction {
  key: string
  label: string
  icon: React.ReactNode
  onClick: () => void
  variant?: 'primary' | 'default' | 'danger'
  disabled?: boolean
  hidden?: boolean
  /** Group separator -- renders a divider before this action */
  group?: boolean
  /** Hide from toolbar (show elsewhere, e.g., footer) */
  hideFromToolbar?: boolean
}

interface AgentNodeToolbarProps {
  actions: ToolbarAction[]
  isVisible: boolean
}

export const AgentNodeToolbar = memo(function AgentNodeToolbar({
  actions,
  isVisible,
}: AgentNodeToolbarProps) {
  const { zoom } = useViewport()
  const visibleActions = actions.filter((a) => !a.hidden && !a.hideFromToolbar)

  if (visibleActions.length === 0) return null

  const scale = Math.min(Math.max(1 / zoom, 0.5), 2)

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale, x: '-50%' }}
          animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
          exit={{ opacity: 0, y: 10, scale, x: '-50%' }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
            opacity: { duration: 0.15 },
          }}
          className="absolute z-50 nopan nodrag"
          style={{
            bottom: '100%',
            left: '50%',
            marginBottom: 8,
            transformOrigin: 'bottom center',
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-2 py-1">
            {visibleActions.map((action, i) => (
              <div key={action.key} className="flex items-center">
                {action.group && i > 0 && (
                  <div className="w-px h-4 bg-border-subtle mx-0.5" />
                )}
                <div className="relative group">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      action.onClick()
                    }}
                    disabled={action.disabled}
                    className={cn(
                      'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
                      action.variant === 'primary' &&
                        'bg-primary/90 text-primary-foreground hover:bg-primary',
                      action.variant === 'danger' &&
                        'text-text-muted hover:text-destructive hover:bg-destructive/10',
                      action.variant !== 'primary' &&
                        action.variant !== 'danger' &&
                        'text-text-muted hover:text-text-primary hover:bg-surface-1',
                      action.disabled && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      {action.icon}
                    </span>
                    {action.variant === 'primary' && (
                      <span>{action.label}</span>
                    )}
                  </button>
                  {/* Instant tooltip */}
                  {action.variant !== 'primary' && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2 py-1 text-xxs text-foreground bg-surface-2 border border-border rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-100">
                      {action.label}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

AgentNodeToolbar.displayName = 'AgentNodeToolbar'
